import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Input, Modal } from 'antd';
import * as heic2anyModule from 'heic2any';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { GalleryResourceTypes } from '../../constants/galleryUpload';
import { useGalleryUpload } from './GalleryUploadContext';

const RESOURCE_CONFIG = {
  [GalleryResourceTypes.PHOTOS]: {
    translationPrefix: 'GALLERY_PHOTO_UPLOAD',
    fallbackPreviewText: 'IMG',
  },
  [GalleryResourceTypes.VIDEOS]: {
    translationPrefix: 'GALLERY_VIDEO_UPLOAD',
    fallbackPreviewText: '▶',
  },
};

const VIDEO_PREVIEW_TIMEOUT_MS = 5000;
const HEIC_PREVIEW_TIMEOUT_MS = 12000;
const VIDEO_MP4_EXTENSION_REGEX = /\.mp4$/i;
const HEIC_PHOTO_EXTENSION_REGEX = /\.(heic|heif)$/i;
const heic2anyConverter = heic2anyModule?.default || heic2anyModule;

const isMp4VideoFile = (file) => {
  if (!file) {
    return false;
  }

  const mimeType = String(file.type || '').toLowerCase();
  const fileName = String(file.name || '');

  return mimeType === 'video/mp4' || VIDEO_MP4_EXTENSION_REGEX.test(fileName);
};

const getCountLabel = (count, trans, resourceType) => {
  const prefix = RESOURCE_CONFIG[resourceType].translationPrefix;
  const key = count === 1 ? `${prefix}_FILE_SELECTED_SINGULAR` : `${prefix}_FILE_SELECTED_PLURAL`;
  return trans(key).replace('{{count}}', `${count}`);
};

const revokePreviewUrls = (items = []) => {
  items.forEach((item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }

    if (
      item?.thumbnailUrl &&
      item.thumbnailUrl !== item.previewUrl &&
      String(item.thumbnailUrl).startsWith('blob:')
    ) {
      URL.revokeObjectURL(item.thumbnailUrl);
    }
  });
};

const isHeicPhotoFile = (file) => {
  if (!file) {
    return false;
  }

  const mimeType = String(file.type || '').toLowerCase();
  const fileName = String(file.name || '');

  return (
    mimeType === 'image/heic' ||
    mimeType === 'image/heif' ||
    HEIC_PHOTO_EXTENSION_REGEX.test(fileName)
  );
};

const getHeicMimeCandidates = (file) => {
  const candidates = [String(file?.type || '').toLowerCase(), 'image/heic', 'image/heif'];
  return Array.from(new Set(candidates.filter(Boolean)));
};

const convertCanvasToJpegBlob = (canvas) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', 0.9);
  });

const generateHeicPhotoPreviewFromImageDecoder = async (file) => {
  if (
    !globalThis?.ImageDecoder ||
    typeof globalThis.ImageDecoder.isTypeSupported !== 'function' ||
    typeof file?.arrayBuffer !== 'function'
  ) {
    return '';
  }

  const sourceBuffer = await file.arrayBuffer();
  const mimeCandidates = getHeicMimeCandidates(file);

  for (const mimeType of mimeCandidates) {
    let decoder;
    let decodedFrame;

    try {
      const isSupported = await globalThis.ImageDecoder.isTypeSupported(mimeType);
      if (!isSupported) {
        continue;
      }

      decoder = new globalThis.ImageDecoder({
        data: sourceBuffer,
        type: mimeType,
      });

      const decodedResult = await decoder.decode({ frameIndex: 0 });
      decodedFrame = decodedResult?.image;

      const width =
        decodedFrame?.displayWidth || decodedFrame?.codedWidth || decodedFrame?.width || 0;
      const height =
        decodedFrame?.displayHeight || decodedFrame?.codedHeight || decodedFrame?.height || 0;

      if (!width || !height) {
        continue;
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        continue;
      }

      canvas.width = width;
      canvas.height = height;
      context.drawImage(decodedFrame, 0, 0, width, height);

      const jpegBlob = await convertCanvasToJpegBlob(canvas);
      if (jpegBlob) {
        return URL.createObjectURL(jpegBlob);
      }
    } catch (error) {
      // Try the next mime candidate or fallback converter.
    } finally {
      if (decodedFrame && typeof decodedFrame.close === 'function') {
        decodedFrame.close();
      }

      if (decoder && typeof decoder.close === 'function') {
        decoder.close();
      }
    }
  }

  return '';
};

const generateHeicPhotoPreviewFromFile = async (file) => {
  try {
    const nativePreviewUrl = await generateHeicPhotoPreviewFromImageDecoder(file);
    if (nativePreviewUrl) {
      return nativePreviewUrl;
    }

    if (typeof heic2anyConverter !== 'function') {
      throw new Error('HEIC converter is unavailable');
    }

    const fileBuffer = await file.arrayBuffer();
    const sourceBlob = new Blob([fileBuffer], { type: file.type || 'image/heic' });
    const convertedResult = await heic2anyConverter({
      blob: sourceBlob,
      toType: 'image/jpeg',
      quality: 0.9,
    });

    const convertedBlob = Array.isArray(convertedResult)
      ? convertedResult[0] || null
      : convertedResult;
    if (!(convertedBlob instanceof Blob)) {
      return '';
    }

    return URL.createObjectURL(convertedBlob);
  } catch (error) {
    console.warn('Failed to convert HEIC preview in upload modal:', error);
    return '';
  }
};

const withTimeoutResult = (promise, timeoutMs) =>
  new Promise((resolve) => {
    let finished = false;

    const done = (value) => {
      if (finished) {
        return;
      }

      finished = true;
      resolve(value);
    };

    const timeoutId = setTimeout(() => {
      done({ timedOut: true, value: '' });
    }, timeoutMs);

    promise
      .then((value) => done({ timedOut: false, value }))
      .catch(() => done({ timedOut: false, value: '' }))
      .finally(() => clearTimeout(timeoutId));
  });

const generateVideoThumbnailFromObjectUrl = (objectUrl, seekSeconds = 0.2, timeoutMs = 5000) =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    let resolved = false;
    let timeoutId;

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      video.pause();
      video.src = '';
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const done = (value) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(value);
      }
    };

    const onLoadedMetadata = () => {
      try {
        const target = Math.min(seekSeconds, Math.max(0.1, video.duration || 1));
        video.currentTime = target;
      } catch (error) {
        done('');
      }
    };

    const onSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context || !video.videoWidth || !video.videoHeight) {
          done('');
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        done(canvas.toDataURL('image/jpeg', 0.82));
      } catch (error) {
        done('');
      }
    };

    const onError = () => done('');

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    video.src = objectUrl;
    video.load();

    timeoutId = setTimeout(() => done(''), timeoutMs);
  });

const generateVideoThumbnailFromFile = async (file) => {
  const objectUrl = URL.createObjectURL(file);

  try {
    let thumbnail = await generateVideoThumbnailFromObjectUrl(
      objectUrl,
      0.2,
      VIDEO_PREVIEW_TIMEOUT_MS
    );
    if (!thumbnail) {
      thumbnail = await generateVideoThumbnailFromObjectUrl(objectUrl, 1, VIDEO_PREVIEW_TIMEOUT_MS);
    }
    return thumbnail;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const ResourceUploadModal = ({
  open,
  onClose,
  accountUuid,
  selectedFilesFromParent,
  resourceType,
}) => {
  const trans = useSelector(translator);
  const { startUploads, isUploadApiAvailable, isShareApiAvailable, uploaderUuid } =
    useGalleryUpload();
  const isMountedRef = useRef(true);
  const selectedFilesRef = useRef([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [hasUploadStarted, setHasUploadStarted] = useState(false);

  const resourceConfig = RESOURCE_CONFIG[resourceType];

  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      revokePreviewUrls(selectedFilesRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const preparePreviews = async () => {
      if (!open || !selectedFilesFromParent || selectedFilesFromParent.length === 0) {
        setSelectedFiles((previousItems) => {
          revokePreviewUrls(previousItems);
          return [];
        });
        return;
      }

      const immediateItems = selectedFilesFromParent.map((file) => {
        const previewUrl = URL.createObjectURL(file);
        const isHeicPhoto = resourceType === GalleryResourceTypes.PHOTOS && isHeicPhotoFile(file);

        return {
          id: `${file.name}-${file.lastModified}-${file.size}`,
          file,
          previewUrl,
          thumbnailUrl:
            resourceType === GalleryResourceTypes.PHOTOS ? (isHeicPhoto ? '' : previewUrl) : '',
          isPreviewLoading: isHeicPhoto,
          hasPreviewError: false,
        };
      });

      setSelectedFiles((previousItems) => {
        revokePreviewUrls(previousItems);
        return immediateItems;
      });

      if (resourceType === GalleryResourceTypes.PHOTOS) {
        immediateItems.forEach((item) => {
          if (!isHeicPhotoFile(item.file)) {
            return;
          }

          const conversionPromise = generateHeicPhotoPreviewFromFile(item.file);

          withTimeoutResult(conversionPromise, HEIC_PREVIEW_TIMEOUT_MS).then((timeoutResult) => {
            if (cancelled || !isMountedRef.current) {
              if (timeoutResult.value) {
                URL.revokeObjectURL(timeoutResult.value);
              }
              return;
            }

            if (timeoutResult.timedOut) {
              setSelectedFiles((previousItems) =>
                previousItems.map((currentItem) =>
                  currentItem.id === item.id
                    ? {
                        ...currentItem,
                        isPreviewLoading: false,
                        hasPreviewError: true,
                      }
                    : currentItem
                )
              );

              conversionPromise
                .then((latePreviewUrl) => {
                  if (!latePreviewUrl || cancelled || !isMountedRef.current) {
                    if (latePreviewUrl) {
                      URL.revokeObjectURL(latePreviewUrl);
                    }
                    return;
                  }

                  setSelectedFiles((previousItems) =>
                    previousItems.map((currentItem) => {
                      if (currentItem.id !== item.id) {
                        return currentItem;
                      }

                      if (
                        currentItem.thumbnailUrl &&
                        currentItem.thumbnailUrl !== currentItem.previewUrl &&
                        String(currentItem.thumbnailUrl).startsWith('blob:')
                      ) {
                        URL.revokeObjectURL(currentItem.thumbnailUrl);
                      }

                      return {
                        ...currentItem,
                        thumbnailUrl: latePreviewUrl,
                        isPreviewLoading: false,
                        hasPreviewError: false,
                      };
                    })
                  );
                })
                .catch(() => {});

              return;
            }

            setSelectedFiles((previousItems) =>
              previousItems.map((currentItem) => {
                if (currentItem.id !== item.id) {
                  return currentItem;
                }

                if (
                  timeoutResult.value &&
                  currentItem.thumbnailUrl &&
                  currentItem.thumbnailUrl !== currentItem.previewUrl &&
                  currentItem.thumbnailUrl !== timeoutResult.value &&
                  String(currentItem.thumbnailUrl).startsWith('blob:')
                ) {
                  URL.revokeObjectURL(currentItem.thumbnailUrl);
                }

                return {
                  ...currentItem,
                  thumbnailUrl: timeoutResult.value || '',
                  isPreviewLoading: false,
                  hasPreviewError: !timeoutResult.value,
                };
              })
            );
          });
        });

        return;
      }

      if (resourceType !== GalleryResourceTypes.VIDEOS) {
        return;
      }

      const previewResults = await Promise.allSettled(
        immediateItems.map(async (item) => {
          const thumbnailUrl = await generateVideoThumbnailFromFile(item.file);

          return {
            ...item,
            thumbnailUrl,
          };
        })
      );

      const previewItems = previewResults.map((result, index) =>
        result.status === 'fulfilled' ? result.value : immediateItems[index]
      );

      if (cancelled || !isMountedRef.current) {
        return;
      }

      setSelectedFiles(previewItems);
    };

    preparePreviews();

    return () => {
      cancelled = true;
    };
  }, [open, resourceType, selectedFilesFromParent]);

  const selectedCountLabel = useMemo(
    () => getCountLabel(selectedFiles.length, trans, resourceType),
    [resourceType, selectedFiles.length, trans]
  );

  const invalidVideoSelections = useMemo(() => {
    if (resourceType !== GalleryResourceTypes.VIDEOS) {
      return [];
    }

    return selectedFiles.filter((selectedFile) => !isMp4VideoFile(selectedFile.file));
  }, [resourceType, selectedFiles]);

  const hasInvalidVideoSelection = invalidVideoSelections.length > 0;

  const resetState = () => {
    setTitle('');
    setDescription('');
    setHasUploadStarted(false);
    setSelectedFiles((previousItems) => {
      revokePreviewUrls(previousItems);
      return [];
    });
  };

  const handleClose = () => {
    if (!isUploading) {
      resetState();
    }

    onClose();
  };

  const handleRemoveSelectedFile = (fileId) => {
    if (isUploading) {
      return;
    }

    setSelectedFiles((previousItems) => {
      const removedItem = previousItems.find((item) => item.id === fileId);
      if (removedItem) {
        revokePreviewUrls([removedItem]);
      }
      return previousItems.filter((item) => item.id !== fileId);
    });
  };

  const handleUploadResources = async () => {
    if (
      selectedFiles.length === 0 ||
      hasInvalidVideoSelection ||
      !isUploadApiAvailable ||
      !isShareApiAvailable ||
      !uploaderUuid ||
      !accountUuid
    ) {
      return;
    }

    setHasUploadStarted(true);
    setIsUploading(true);

    try {
      const { failedCount = 0 } =
        (await startUploads({
          selectedFiles,
          resourceType,
          accountUuid,
          title,
          description,
        })) || {};

      if (failedCount === 0 && isMountedRef.current) {
        resetState();
        onClose();
      }
    } catch (error) {
      console.error(`${resourceType} upload failed:`, error);
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false);
      }
    }
  };

  const invalidVideoFileNames = invalidVideoSelections
    .map((selectedFile) => selectedFile?.file?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <Modal
      className="gallery-video-upload-modal"
      open={open}
      centered
      footer={null}
      closable
      onCancel={handleClose}
      maskClosable
      width={480}
    >
      <div className="gallery-video-upload-content">
        <div className="gallery-video-upload-count">{selectedCountLabel}</div>

        <div className="gallery-video-upload-field">
          <label htmlFor="gallery-resource-title">
            {trans(`${resourceConfig.translationPrefix}_TITLE_LABEL`)}
          </label>
          <Input
            id="gallery-resource-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={trans(`${resourceConfig.translationPrefix}_TITLE_PLACEHOLDER`)}
            maxLength={100}
            disabled={isUploading}
          />
        </div>

        <div className="gallery-video-upload-field">
          <label htmlFor="gallery-resource-description">
            {trans(`${resourceConfig.translationPrefix}_DESCRIPTION_LABEL`)}
          </label>
          <Input
            id="gallery-resource-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={trans(`${resourceConfig.translationPrefix}_DESCRIPTION_PLACEHOLDER`)}
            maxLength={250}
            disabled={isUploading}
          />
        </div>

        <div className="gallery-video-upload-actions">
          <Button
            type="primary"
            className="gallery-video-upload-submit-btn"
            onClick={handleUploadResources}
            loading={isUploading}
            disabled={
              selectedFiles.length === 0 ||
              hasInvalidVideoSelection ||
              !isUploadApiAvailable ||
              !isShareApiAvailable ||
              !uploaderUuid ||
              !accountUuid
            }
          >
            {trans('UPLOAD')}
          </Button>
        </div>

        {hasInvalidVideoSelection && (
          <div className="gallery-video-upload-validation-error" role="alert">
            {trans('GALLERY_VIDEO_UPLOAD_MP4_ONLY_ERROR')}
            {invalidVideoFileNames ? `: ${invalidVideoFileNames}` : ''}
          </div>
        )}

        {resourceType === GalleryResourceTypes.VIDEOS && hasUploadStarted ? (
          <div className="gallery-video-upload-large-file-hint">
            {trans('GALLERY_VIDEO_UPLOAD_LARGE_FILE_HINT')}
          </div>
        ) : null}

        {selectedFiles.length > 0 ? (
          <div className="gallery-video-upload-preview-grid">
            {selectedFiles.map((selectedFile) => (
              <div key={selectedFile.id} className="gallery-video-upload-preview-card">
                <button
                  type="button"
                  className="gallery-video-upload-preview-remove-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveSelectedFile(selectedFile.id);
                  }}
                  aria-label={trans('REMOVE')}
                  disabled={isUploading}
                >
                  <CloseOutlined />
                </button>

                <div className="gallery-video-upload-preview-media-wrapper">
                  {selectedFile.thumbnailUrl ? (
                    <img
                      src={selectedFile.thumbnailUrl}
                      alt={selectedFile.file.name}
                      className="gallery-video-upload-preview-image"
                    />
                  ) : resourceType === GalleryResourceTypes.VIDEOS && selectedFile.previewUrl ? (
                    <video
                      src={selectedFile.previewUrl}
                      className="gallery-video-upload-preview-image"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <div
                      className={`gallery-video-upload-preview-fallback ${
                        resourceType === GalleryResourceTypes.PHOTOS
                          ? 'gallery-video-upload-preview-fallback-photos'
                          : 'gallery-video-upload-preview-fallback-videos'
                      } ${selectedFile.isPreviewLoading ? 'is-loading' : ''}`}
                    >
                      {resourceType === GalleryResourceTypes.PHOTOS ? (
                        <span className="gallery-photo-skeleton-icon" />
                      ) : (
                        <span className="gallery-video-skeleton-bar" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

ResourceUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountUuid: PropTypes.string,
  selectedFilesFromParent: PropTypes.arrayOf(PropTypes.object),
  resourceType: PropTypes.oneOf(Object.values(GalleryResourceTypes)).isRequired,
};

export default ResourceUploadModal;
